import React, { useCallback, useEffect, useMemo, useState } from "react";

import Alert from "veneer/Alert";
import AutocompleteSelectInput from "veneer/AutocompleteSelectInput";
import Box from "veneer/Box";
import Button from "veneer/Button";
import Drawer from "veneer/Drawer";
import FormControlLabel from "veneer/FormControlLabel";
import RadioGroup from "veneer/RadioGroup";
import { showError, showSuccess } from "veneer/SnackBar";
import Stack from "veneer/Stack";
import { CheckFilledIcon, ErrorStopSignOutlinedIcon } from "veneer/SvgIcon";
import { Colors } from "veneer/Theme";
import Tooltip from "veneer/Tooltip";
import Typography from "veneer/Typography";

import { RELEASE_NOTES_URL } from "cortex/ui/src/constant/app";
import {
  upgradableKmpPlayerVersionsSelector,
  upgradablePlayerVersionsSelector,
  upgradableWebOsPlayerVersionsSelector,
} from "cortex/ui/src/fleet/redux-store/ui-state/player-versions/selectors";
import { Device } from "cortex/ui/src/fleet/view/device/model";
import { compareVersion } from "cortex/ui/src/fleet/view/device/utils";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import {
  DOWNGRADE_DEVICES,
  UPGRADE_DEVICES,
} from "cortex/ui/src/redux-store/devices/actions";
import { isKmpPlayer } from "cortex/ui/src/redux-store/devices/selectors";
import { playerReleaseService } from "cortex/ui/src/service/playerRelease";
import RadioButton from "cortex/ui/src/ui/radio-button";
import { Option } from "cortex/ui/src/ui/table/types";
import { download } from "cortex/ui/src/util/download";

import BaseDrawer, {
  ErrorListItem,
  ListItem,
  ListWrapper,
  UpgradeDrawerProps,
  UpgradeEligibility,
  UpgradeRow,
} from "./BaseDrawer";

interface UpgradeRowWithVersion extends UpgradeRow {
  targetVersion: string;
}

const OPERATIONS = {
  ANDROID: "android",
  UPGRADE: "upgrade",
  UPGRADE_WEBOS: "upgrade_webos",
  DOWNGRADE: "downgrade",
  DOWNGRADE_WEBOS: "downgrade_webos",
  KMP: "kmp",
};

const OPERATING_SYSTEMS = {
  ANDROID: "android",
  WEBOS: "webos",
  WINDOWS: "win32",
  LINUX: "linux",
};

type Operation = (typeof OPERATIONS)[keyof typeof OPERATIONS];

type FormState = {
  operation: Operation;
  targetVersion: string | null;
};

type ErrorState<T> = Partial<Record<keyof T, string>>;
const EMPTY_VERSION_OPTION: Option<string> = {
  label: "",
  value: "",
};

const isWebOs = (device: Device) =>
  device.os_platform === OPERATING_SYSTEMS.WEBOS;

const isLinuxWindows = (device: Device) =>
  device.os_platform === OPERATING_SYSTEMS.LINUX ||
  device.os_platform === OPERATING_SYSTEMS.WINDOWS;

const isAndroid = (device: Device) =>
  device.os_platform === OPERATING_SYSTEMS.ANDROID;

// KMP reports os_platform free-form ("windows 11" where nwjs says "win32"),
// so it is matched on player_type. Android KMP uses the download flow above.
const isKmpPush = (device: Device) => isKmpPlayer(device) && !isAndroid(device);

const isWebOsOperation = (operation: Operation) =>
  operation === OPERATIONS.UPGRADE_WEBOS ||
  operation === OPERATIONS.DOWNGRADE_WEBOS;

const isDowngradeOperation = (operation: Operation) =>
  operation === OPERATIONS.DOWNGRADE ||
  operation === OPERATIONS.DOWNGRADE_WEBOS;

interface AndroidRelease {
  url: string;
  version: string;
}

function getAndroidReleaseInfo(
  latestVersions: Record<string, string>,
): AndroidRelease | null {
  const version =
    latestVersions["kmpPlayer-version"] ?? latestVersions.version ?? "";
  const url =
    latestVersions["kmpPlayer/android/arm64/url"] ??
    latestVersions["player/android/arm64/url"] ??
    "";

  if (!version || !url) {
    return null;
  }

  return {
    url,
    version,
  };
}

function invalid(reason: string): UpgradeEligibility {
  return { isValid: false, reason };
}

function getUpgradeEligibility(
  device: Device,
  operation: Operation,
  targetVersion: string,
): UpgradeEligibility {
  const isDowngrade = isDowngradeOperation(operation);
  const isUpgrade = operation === OPERATIONS.UPGRADE;
  const isWebOsUpgrade = operation === OPERATIONS.UPGRADE_WEBOS;
  const isWebOsOp = isWebOsOperation(operation);
  const linuxWindows = isLinuxWindows(device);
  const webOs = isWebOs(device);

  if (device.device_update_disabled) {
    return invalid("Remote updates are disabled for this device");
  }

  // Exact strings only: the nwjs checks below are semver rules that a KMP
  // "+build-hash" cannot satisfy.
  if (operation === OPERATIONS.KMP) {
    if (!isKmpPush(device)) {
      return invalid("Only KMP players can be updated using this operation");
    }

    if (!targetVersion) {
      return invalid("No target build selected");
    }

    if (device.current_version === targetVersion) {
      return invalid(
        `Device is already on this build: ${device.current_version}`,
      );
    }

    if (device.target_version === targetVersion) {
      return invalid(
        `Device is already pending an update to build ${device.target_version}`,
      );
    }

    return { isValid: true };
  }

  if (isKmpPush(device)) {
    return invalid("KMP players must be updated using the KMP operation");
  }

  if (
    device.network_version &&
    (isUpgrade || isDowngrade || isWebOsUpgrade) &&
    compareVersion(targetVersion, device.network_version) > 0
  ) {
    return invalid(
      `Network settings restrict player upgrade above version ${device.network_version}`,
    );
  }

  if (
    isDowngrade &&
    linuxWindows &&
    compareVersion(device.current_version, "3.0.0") < 0
  ) {
    return invalid(
      `Downgrades are not available on this version: ${device.current_version}`,
    );
  }

  if (!targetVersion) {
    return invalid("No target version selected");
  }

  if (device.target_version === targetVersion) {
    return invalid(
      `Device is already pending upgrade/downgrade to version ${device.target_version}`,
    );
  }

  if (isWebOsOp && !webOs) {
    return invalid(
      "Only webOS devices can be upgraded/downgraded using this operation",
    );
  }

  if (!isWebOsOp && !linuxWindows) {
    return invalid(
      "Only Linux and Windows devices can be upgraded/downgraded using this operation",
    );
  }

  const versionComparison = compareVersion(
    device.current_version,
    targetVersion,
  );

  if ((isUpgrade || isWebOsUpgrade) && versionComparison >= 0) {
    return invalid(
      `Device is already on this or a newer version: ${device.current_version}`,
    );
  }

  if (isDowngrade && versionComparison <= 0) {
    return invalid(
      `Device is already on this or an older version: ${device.current_version}`,
    );
  }

  if (isDowngrade && !device.can_downgrade) {
    return invalid("This device cannot be downgraded to the selected version");
  }

  return { isValid: true };
}

function getTargetVersionForDevice(
  device: Device,
  operation: Operation,
  selectedTargetVersion: string | null,
) {
  if (operation === OPERATIONS.UPGRADE_WEBOS) {
    return selectedTargetVersion ?? device.latest_version ?? "";
  }
  return selectedTargetVersion ?? "";
}

function getTargetVersionHelperText(operation: Operation) {
  if (operation === OPERATIONS.UPGRADE_WEBOS) {
    return null;
  }

  if (operation === OPERATIONS.KMP) {
    return "Devices already on, or already pending, the selected build will be skipped.";
  }

  if (isDowngradeOperation(operation)) {
    return "Only devices using a newer version will be downgraded.";
  }

  return "Only devices using an older version will be upgraded.";
}

export default function UpDowngradePlayerDrawer({
  onCancel,
  open,
  selectedItems,
  setSelectedItems,
}: UpgradeDrawerProps) {
  const dispatch = useAppDispatch();
  const availableVersions = useAppSelector(upgradablePlayerVersionsSelector);
  const availableKmpVersions = useAppSelector(
    upgradableKmpPlayerVersionsSelector,
  );
  const availableWebOsVersions = useAppSelector(
    upgradableWebOsPlayerVersionsSelector,
  );
  const [errors, setErrors] = useState<ErrorState<FormState>>({});
  const [submitted, setSubmitted] = useState(false);
  const [androidRelease, setAndroidRelease] = useState<AndroidRelease | null>(
    null,
  );
  const [androidReleaseLoading, setAndroidReleaseLoading] = useState(false);
  const [upgradeRows, setUpgradeRows] = useState<UpgradeRowWithVersion[]>([]);
  const [form, setForm] = useState<FormState>({
    operation: OPERATIONS.UPGRADE,
    targetVersion: null,
  });
  const playerRows = useMemo(
    () =>
      selectedItems
        .filter(
          (device) =>
            !device.is_agent &&
            (isKmpPush(device) ||
              isAndroid(device) ||
              isLinuxWindows(device) ||
              isWebOs(device)),
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [selectedItems],
  );

  const { onlyWebOs, onlyLinuxWindows } = useMemo(() => {
    if (playerRows.length === 0)
      return { onlyWebOs: false, onlyLinuxWindows: false };

    let onlyWebOs = true;
    let onlyLinuxWindows = true;

    for (const device of playerRows) {
      if (!isWebOs(device)) onlyWebOs = false;
      if (!isLinuxWindows(device)) onlyLinuxWindows = false;
      if (!onlyWebOs && !onlyLinuxWindows) break;
    }

    return { onlyWebOs, onlyLinuxWindows };
  }, [playerRows]);

  const hasAndroidPlayers = useMemo(
    () => playerRows.some(isAndroid),
    [playerRows],
  );

  const onlyAndroidPlayers = useMemo(
    () => playerRows.length > 0 && playerRows.every(isAndroid),
    [playerRows],
  );

  const hasKmpPlayers = useMemo(() => playerRows.some(isKmpPush), [playerRows]);

  const onlyKmpPlayers = useMemo(
    () => playerRows.length > 0 && playerRows.every(isKmpPush),
    [playerRows],
  );

  const targetWebOsVersion = useMemo(() => {
    const webosLatest = playerRows
      .filter(isWebOs)
      .map((device) => device.latest_version ?? "0.0.0");

    if (webosLatest.length === 0) return null;

    webosLatest.sort((a, b) => compareVersion(b, a));
    return webosLatest[0];
  }, [playerRows]);

  const versionOptions: ReadonlyArray<Option<string>> = useMemo(() => {
    const versions =
      form.operation === OPERATIONS.KMP
        ? availableKmpVersions
        : isWebOsOperation(form.operation)
          ? availableWebOsVersions
          : availableVersions;

    return versions.map((version: string) => ({
      label: version,
      value: version,
    }));
  }, [
    availableKmpVersions,
    availableVersions,
    availableWebOsVersions,
    form.operation,
  ]);

  const selectedVersionOption = useMemo(() => {
    if (form.targetVersion) {
      return (
        versionOptions.find((opt) => opt.value === form.targetVersion) ?? {
          label: form.targetVersion,
          value: form.targetVersion,
        }
      );
    }

    if (form.operation === OPERATIONS.UPGRADE_WEBOS && targetWebOsVersion) {
      return {
        label: targetWebOsVersion,
        value: targetWebOsVersion,
      };
    }

    return EMPTY_VERSION_OPTION;
  }, [form.operation, form.targetVersion, targetWebOsVersion, versionOptions]);

  useEffect(() => {
    if (!open) return;

    setForm((prev) => {
      let operation = prev.operation;

      if (onlyAndroidPlayers) {
        operation = OPERATIONS.ANDROID;
      } else if (onlyKmpPlayers) {
        operation = OPERATIONS.KMP;
      } else if (onlyWebOs) {
        operation = OPERATIONS.UPGRADE_WEBOS;
      } else if (!hasAndroidPlayers && operation === OPERATIONS.ANDROID) {
        operation = OPERATIONS.UPGRADE;
      } else if (!hasKmpPlayers && operation === OPERATIONS.KMP) {
        operation = OPERATIONS.UPGRADE;
      } else if (onlyLinuxWindows && isWebOsOperation(operation)) {
        operation = OPERATIONS.UPGRADE;
      }

      const streamChanged =
        (operation === OPERATIONS.KMP) !== (prev.operation === OPERATIONS.KMP);

      return {
        ...prev,
        operation,
        targetVersion:
          operation === OPERATIONS.UPGRADE_WEBOS
            ? targetWebOsVersion
            : streamChanged
              ? null
              : prev.targetVersion,
      };
    });
  }, [
    hasAndroidPlayers,
    hasKmpPlayers,
    onlyAndroidPlayers,
    onlyKmpPlayers,
    onlyLinuxWindows,
    onlyWebOs,
    open,
    targetWebOsVersion,
  ]);

  useEffect(() => {
    if (!open) {
      setAndroidRelease(null);
      setAndroidReleaseLoading(false);
      return;
    }

    if (form.operation !== OPERATIONS.ANDROID || !hasAndroidPlayers) {
      return;
    }

    let active = true;

    const loadAndroidRelease = async () => {
      setAndroidReleaseLoading(true);

      try {
        const response = await playerReleaseService.getLatestVersions();
        const latestVersions =
          (response as any)?.data?.data ??
          (response as any)?.data ??
          (response as any) ??
          {};
        const release = getAndroidReleaseInfo(
          latestVersions as Record<string, string>,
        );

        if (!release) {
          throw new Error("Android player download information is unavailable");
        }

        if (active) {
          setAndroidRelease(release);
        }
      } catch (_error) {
        if (active) {
          setAndroidRelease(null);
          showError(
            "An error occurred while loading the Android player download link. Please try again.",
          );
        }
      } finally {
        if (active) {
          setAndroidReleaseLoading(false);
        }
      }
    };

    void loadAndroidRelease();

    return () => {
      active = false;
    };
  }, [form.operation, hasAndroidPlayers, open]);

  useEffect(() => {
    const rows = playerRows.map((device) => {
      const targetVersion = getTargetVersionForDevice(
        device,
        form.operation,
        form.targetVersion,
      );

      return {
        device,
        targetVersion,
        ...getUpgradeEligibility(device, form.operation, targetVersion),
      };
    });

    setUpgradeRows(rows);
  }, [form.operation, form.targetVersion, playerRows]);

  const validDeviceIds = useMemo(
    () => upgradeRows.filter((row) => row.isValid).map((row) => row.device.id),
    [upgradeRows],
  );

  const commitLabel = isDowngradeOperation(form.operation)
    ? "Downgrade Player"
    : form.operation === OPERATIONS.KMP
      ? "Upgrade KMP Player"
      : "Upgrade Player";
  const isAndroidOperation = form.operation === OPERATIONS.ANDROID;
  const showDeviceList = !isAndroidOperation;

  const handleOperationChange = useCallback(
    (_e: unknown, value: Operation) => {
      setForm((prev) => ({
        ...prev,
        operation: value,
        targetVersion:
          value === OPERATIONS.UPGRADE_WEBOS ? targetWebOsVersion : null,
      }));
      if (errors.targetVersion) {
        clearError("targetVersion");
      }
    },
    [errors.targetVersion, targetWebOsVersion],
  );

  const handleTargetVersionChange = useCallback(
    (_e: unknown, opt: Option<string>) => {
      setForm((prev) => ({
        ...prev,
        targetVersion: opt.value,
      }));
      if (errors.targetVersion) {
        clearError("targetVersion");
      }
    },
    [errors.targetVersion],
  );

  const handlePlayerAction = useCallback(async () => {
    if (isAndroidOperation) {
      return;
    }

    if (!form.targetVersion) {
      setErrors({ targetVersion: "This field is required" });
      return;
    }

    if (validDeviceIds.length === 0) {
      return;
    }

    setSubmitted(true);

    const action = isDowngradeOperation(form.operation)
      ? DOWNGRADE_DEVICES
      : UPGRADE_DEVICES;
    const actionLabel = isDowngradeOperation(form.operation)
      ? "downgraded"
      : "upgraded";
    try {
      await createPromiseApiAction(dispatch, action, {
        deviceIds: validDeviceIds,
        targetVersion: form.targetVersion,
      });
      showSuccess(
        `Request processed successfully. Your ${validDeviceIds.length} device(s) will be ${actionLabel} shortly.`,
      );
    } catch (e) {
      showError(
        "An error occurred while attempting to process your request. Please try again.",
      );
    } finally {
      setSelectedItems([]);
      setSubmitted(false);
      onCancel();
    }
  }, [
    dispatch,
    form.operation,
    form.targetVersion,
    onCancel,
    setSelectedItems,
    validDeviceIds,
  ]);

  const handleAndroidDownload = useCallback(() => {
    if (!androidRelease?.url) {
      showError(
        "The Android player download link is not available right now. Please try again.",
      );
      return;
    }

    download(androidRelease.url, `Player-v${androidRelease.version}.apk`);
  }, [androidRelease]);

  const androidDownloadCopy = useMemo(() => {
    if (androidReleaseLoading) {
      return "Loading the latest Android player version...";
    }

    if (androidRelease) {
      return (
        <>
          Download the .apk for Player <b>v{androidRelease.version}</b>, then
          upload it to Esper, add it to your Blueprint, and converge the
          Blueprint to your devices.
        </>
      );
    }

    return "The Android player download link is unavailable right now.";
  }, [androidRelease, androidReleaseLoading]);

  const clearError = useCallback((key: keyof FormState) => {
    setErrors((prev) => {
      const { [key]: _omit, ...rest } = prev;
      return rest;
    });
  }, []);

  const drawerHeader = (
    <>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        spacing={1}
      >
        <Box width="66%">
          <Typography color={Colors.gray1000} variant="body2">
            Review player release notes and monitor the performance of a limited
            set of players before a wider network rollout.
          </Typography>
        </Box>
        <Box minWidth="fit-content">
          <Button
            icon="openInNew"
            iconPosition="end"
            onClick={() => {
              window.open(RELEASE_NOTES_URL, "_blank");
            }}
            variant="text"
          >
            Release Notes
          </Button>
        </Box>
      </Stack>
      <Stack spacing={0.5}>
        <Typography>Player Operation</Typography>
        <RadioGroup onChange={handleOperationChange} value={form.operation}>
          <FormControlLabel
            control={
              <RadioButton
                disabled={onlyWebOs || onlyAndroidPlayers || onlyKmpPlayers}
              />
            }
            label="Upgrade Player (Linux and Windows)"
            value={OPERATIONS.UPGRADE}
          />
          <FormControlLabel
            control={
              <RadioButton
                disabled={onlyWebOs || onlyAndroidPlayers || onlyKmpPlayers}
              />
            }
            label="Downgrade Player (Linux and Windows)"
            value={OPERATIONS.DOWNGRADE}
          />
          <FormControlLabel
            control={
              <RadioButton
                disabled={
                  onlyLinuxWindows || onlyAndroidPlayers || onlyKmpPlayers
                }
              />
            }
            label="Upgrade (webOS) player"
            value={OPERATIONS.UPGRADE_WEBOS}
          />
          <FormControlLabel
            control={
              <RadioButton
                disabled={
                  onlyLinuxWindows || onlyAndroidPlayers || onlyKmpPlayers
                }
              />
            }
            label="Downgrade (webOS) player"
            value={OPERATIONS.DOWNGRADE_WEBOS}
          />
          <FormControlLabel
            control={<RadioButton disabled={!hasAndroidPlayers} />}
            label="Upgrade (Android) Player"
            value={OPERATIONS.ANDROID}
          />
          {hasKmpPlayers && (
            <FormControlLabel
              control={<RadioButton />}
              label="Upgrade KMP Player"
              value={OPERATIONS.KMP}
            />
          )}
        </RadioGroup>
      </Stack>
      {!isAndroidOperation && (
        <AutocompleteSelectInput
          disableClearable
          inputProps={{
            error: Boolean(errors.targetVersion),
            helperText:
              errors.targetVersion ||
              getTargetVersionHelperText(form.operation),
            label:
              form.operation === OPERATIONS.KMP
                ? "New KMP Player Build"
                : "New Player Version",
          }}
          onChange={handleTargetVersionChange}
          options={
            versionOptions.some(
              (opt) => opt.value === selectedVersionOption.value,
            )
              ? versionOptions
              : [...versionOptions, selectedVersionOption]
          }
          value={selectedVersionOption}
        />
      )}
      {showDeviceList ? (
        <>
          <Alert dense severity="warning" title="Double-check the player list">
            {form.operation === OPERATIONS.UPGRADE_WEBOS ? (
              <>
                You <b>will not</b> be able to revert this change.
              </>
            ) : (
              <>
                You <b>may not</b> be able to revert this change, only limited
                versions of the player will be available to downgrade to.
              </>
            )}
          </Alert>
        </>
      ) : (
        <Stack
          alignItems="center"
          dataTestId="updowngrade-player-android-download"
          justifyContent="center"
          spacing={2}
          textAlign="center"
          width="100%"
        >
          <Typography color={Colors.gray1000}>{androidDownloadCopy}</Typography>
          <Button
            dataTestId="updowngrade-player-android-download-button"
            disabled={androidReleaseLoading || !androidRelease?.url}
            icon="download"
            onClick={handleAndroidDownload}
          >
            Download .apk
          </Button>
        </Stack>
      )}
    </>
  );

  return (
    <Drawer
      commitDisabled={submitted || isAndroidOperation}
      commitLabel={commitLabel}
      commitLoading={submitted}
      onCancel={onCancel}
      onClose={onCancel}
      onCommit={handlePlayerAction}
      open={open}
      padding={0}
      title="Up/Downgrade Player"
    >
      <BaseDrawer drawerHeader={drawerHeader}>
        {showDeviceList &&
          upgradeRows.map(({ device, isValid, reason, targetVersion }) => (
            <ListWrapper disablePadding key={device.id}>
              <UpgradeListItem
                device={device}
                isValid={isValid}
                reason={reason}
                targetVersion={targetVersion}
              />
            </ListWrapper>
          ))}
      </BaseDrawer>
    </Drawer>
  );
}

const UpgradeListItem = ({
  device,
  isValid,
  reason,
  targetVersion,
}: UpgradeRowWithVersion) => {
  const ListComponent = isValid ? ListItem : ErrorListItem;

  return (
    <ListComponent>
      <Stack alignItems="center" direction="row" spacing={2}>
        {isValid ? (
          <CheckFilledIcon color="success" />
        ) : (
          <ErrorStopSignOutlinedIcon color="error" />
        )}
        <Stack flex={1}>
          <Tooltip title={device.device_id}>
            <Typography color={Colors.gray1000} variant="body1">
              {device.name}
            </Typography>
          </Tooltip>
          <Typography variant="body2">
            {isValid && `Player: ${device.current_version} → ${targetVersion}`}
            {!isValid && reason && `${reason}`}
          </Typography>
        </Stack>
      </Stack>
    </ListComponent>
  );
};
